import { attempt } from "@omni/utils/function.utils";
import React, { useEffect } from "react";
import { Path, useFormContext } from "react-hook-form";

import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormFeedback } from "@/components/Form/Base/formFeedback.component";
import { FormText } from "@/components/Form/Base/formText.component";
import { MicroCopy } from "@/components/Form/Base/microCopy.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { isEqual, parseDate } from "@/utils/date";
import { KeyNames } from "@/utils/enums";
import { padStart } from "@/utils/textUtils";

import { useFormErrors } from "../../Utils/useFormErrors.hook";
import { BaseFieldProps } from "../BaseField/BaseField.component";
import { FormFieldProps, isFormRequired } from "../formField.types";
import { NumericKeyHandler } from "../NumericField/NumericField.hooks";
import { NumericTextField } from "../NumericTextField/NumericTextfield.component";
import { DateOfBirthFieldValues } from "./dateOfBirthField.schema";

type DateOfBirthFieldProperties = {};

export type DateOfBirthFieldProps<T extends DateOfBirthFieldValues> =
    FormFieldProps<
        Omit<
            BaseFieldProps<DateOfBirthFieldValues, "dateOfBirth">,
            "name" | "required" | "label"
        > &
            DateOfBirthFieldProperties & {
                required: isFormRequired<T, Extract<Path<T>, "dateOfBirth">>;
            },
        T
    >;

const DateOfBirthField = <T extends DateOfBirthFieldValues = never>({
    required,
    microCopy
}: DateOfBirthFieldProps<T>) => {
    const { t } = useGranite();
    const { setValue, watch } = useFormContext<DateOfBirthFieldValues>();

    const dayInputRef = React.useRef<HTMLInputElement | null>(null);
    const monthInputRef = React.useRef<HTMLInputElement | null>(null);
    const yearInputRef = React.useRef<HTMLInputElement | null>(null);

    const { error } = useFormErrors<DateOfBirthFieldValues>("dateOfBirth");

    const { dateOfBirth, dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear } =
        watch();

    useEffect(() => {
        const formattedDay = padStart(dateOfBirthDay ?? "", 2, "0");
        const formattedMonth = padStart(dateOfBirthMonth ?? "", 2, "0");
        const formattedYear = padStart(dateOfBirthYear ?? "", 4, "0");
        const formattedDate = `${formattedYear}-${formattedMonth}-${formattedDay}`;

        const newDate = attempt(() => {
            const date = parseDate(formattedDate, "yyyy-MM-dd");

            return Number.isNaN(date.getTime()) ? undefined : date;
        }, undefined);

        if (newDate === undefined && dateOfBirth !== undefined) {
            setValue("dateOfBirth", undefined, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
        } else if (!isEqual(dateOfBirth, newDate)) {
            setValue("dateOfBirth", newDate, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true
            });
        }
    }, [
        dateOfBirth,
        dateOfBirthDay,
        dateOfBirthMonth,
        dateOfBirthYear,
        setValue
    ]);

    const onKeyDownHandler =
        (tabFrom: React.RefObject<HTMLInputElement | null>) =>
        (e: React.KeyboardEvent<HTMLInputElement>) =>
        (cb: NumericKeyHandler) => {
            const { value = "" } = e.currentTarget;
            if (
                value.length === 0 &&
                e.key === KeyNames.BACKSPACE &&
                tabFrom?.current
            ) {
                tabFrom.current.focus();
            }

            cb(e);
        };

    const onKeyUpHandler =
        (tabTo: React.RefObject<HTMLInputElement | null>, maxLength: number) =>
        (e: React.KeyboardEvent<HTMLInputElement>) =>
        (cb: NumericKeyHandler) => {
            const { value = "" } = e.currentTarget;

            const ignoredKeys = [
                KeyNames.ARROW_DOWN,
                KeyNames.ARROW_UP,
                KeyNames.TAB,
                KeyNames.SHIFT,
                KeyNames.CTRL,
                KeyNames.ALT
            ];

            if (ignoredKeys.includes(e.key as KeyNames)) {
                return;
            }

            if (value.length === maxLength && tabTo?.current) {
                tabTo.current.focus();
            }

            cb(e);
        };

    const onBlurHandler =
        (maxLength: number) =>
        (e: React.FocusEvent<HTMLInputElement>, cb: () => void) => {
            const { value = "", name } = e.currentTarget;

            if (value.length > 0 && value.length < maxLength) {
                setValue(
                    name as Path<DateOfBirthFieldValues>,
                    padStart(value, maxLength, "0"),
                    {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true
                    }
                );
            }

            cb();
        };

    return (
        <>
            <Row>
                <Col>
                    <FieldLabel
                        name={"dateOfBirth"}
                        label={t("general.form.customer.dateOfBirth.label")}
                        required={required}
                    />
                    {microCopy &&
                        (microCopy instanceof Function ? (
                            microCopy()
                        ) : (
                            <FormText
                                inline={true}
                                color="default"
                            >
                                {microCopy}
                            </FormText>
                        ))}
                    {!microCopy && <MicroCopy fieldId={"dateOfBirth"} />}
                </Col>
            </Row>
            <Row>
                <Col sm={4}>
                    <NumericTextField<DateOfBirthFieldValues>
                        name={"dateOfBirthDay"}
                        label={t("general.form.customer.dateOfBirth.day.label")}
                        placeholder={t(
                            "general.form.customer.dateOfBirth.day.placeholder"
                        )}
                        required={required}
                        ref={dayInputRef}
                        maxLength={2}
                        autoComplete={"bday-day"}
                        onBlur={onBlurHandler(2)}
                        onKeyUp={onKeyUpHandler(monthInputRef, 2)}
                    />
                </Col>
                <Col sm={4}>
                    <NumericTextField<DateOfBirthFieldValues>
                        name={"dateOfBirthMonth"}
                        label={t(
                            "general.form.customer.dateOfBirth.month.label"
                        )}
                        placeholder={t(
                            "general.form.customer.dateOfBirth.month.placeholder"
                        )}
                        required={required}
                        ref={monthInputRef}
                        maxLength={2}
                        autoComplete={"bday-month"}
                        onBlur={onBlurHandler(2)}
                        onKeyDown={onKeyDownHandler(dayInputRef)}
                        onKeyUp={onKeyUpHandler(yearInputRef, 2)}
                    />
                </Col>
                <Col sm={4}>
                    <NumericTextField<DateOfBirthFieldValues>
                        name={"dateOfBirthYear"}
                        label={t(
                            "general.form.customer.dateOfBirth.year.label"
                        )}
                        placeholder={t(
                            "general.form.customer.dateOfBirth.year.placeholder"
                        )}
                        required={required}
                        ref={yearInputRef}
                        maxLength={4}
                        autoComplete={"bday-year"}
                        onBlur={onBlurHandler(4)}
                        onKeyDown={onKeyDownHandler(monthInputRef)}
                    />
                </Col>
            </Row>
            {error && (
                <Row>
                    <Col>
                        <FormFeedback className="d-block">
                            {error.message(
                                t(
                                    "general.form.customer.dateOfBirth.label"
                                ).toLowerCase()
                            )(t)}
                        </FormFeedback>
                    </Col>
                </Row>
            )}
        </>
    );
};

export { DateOfBirthField };
